import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import * as Sentry from "@sentry/react";
import { OfflineSyncService, type FlushRejection, type FlushResult } from "@relais/offline";
import { ApiError } from "./api.js";
import { useSession } from "./auth.js";
import { apiSyncTransport } from "./sync-transport.js";
import { sentrySyncObserver } from "./sync-telemetry.js";
import {
  clearSentryUser,
  setConnectionStateTag,
  setSentryUser,
} from "./sentry.js";

export type ConnectionState = "online" | "offline" | "server-unavailable";

export type SyncError = FlushRejection & {
  id: string;
  at: number;
};

export type Toast = {
  id: string;
  kind: "success" | "info" | "error";
  message: string;
  at: number;
};

type OfflineCtx = {
  service: OfflineSyncService | null;
  queueLength: number;
  tick: number;
  online: boolean;
  serverReachable: boolean;
  connectionState: ConnectionState;
  lastSync: string | null;
  syncErrors: SyncError[];
  dismissSyncError: (id: string) => void;
  toasts: Toast[];
  pushToast: (t: Omit<Toast, "id" | "at">) => void;
  dismissToast: (id: string) => void;
  refresh: () => Promise<void>;
  flush: () => Promise<void>;
  enqueue: (m: Parameters<OfflineSyncService["enqueue"]>[0]) => Promise<string>;
};

const Ctx = createContext<OfflineCtx | null>(null);

// `navigator.onLine` ne détecte ni wifi captif ni API down. On classifie les
// erreurs de la loop pour distinguer "vraiment hors-ligne" de "serveur HS".
function isServerUnreachable(err: unknown): boolean {
  if (err instanceof TypeError) return true; // fetch failure (DNS, CORS, réseau)
  if (err instanceof ApiError && err.status >= 500) return true;
  return false;
}

export function OfflineProvider({ children }: PropsWithChildren) {
  const session = useSession();
  const [service, setService] = useState<OfflineSyncService | null>(null);
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  const [serverReachable, setServerReachable] = useState(true);
  const [queueLength, setQueueLength] = useState(0);
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [syncErrors, setSyncErrors] = useState<SyncError[]>([]);
  const [tick, setTick] = useState(0);
  const bumpTick = useCallback(() => setTick((n) => n + 1), []);
  const errSeqRef = useRef(0);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastSeqRef = useRef(0);

  const pushToast = useCallback(
    (t: Omit<Toast, "id" | "at">) => {
      const id = `${Date.now()}-${toastSeqRef.current++}`;
      setToasts((prev) => [...prev, { ...t, id, at: Date.now() }].slice(-5));
    },
    [],
  );

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    if (toasts.length === 0) return;
    const t = setTimeout(() => {
      const cutoff = Date.now() - 4_000;
      setToasts((prev) => prev.filter((x) => x.at > cutoff));
    }, 4_000);
    return () => clearTimeout(t);
  }, [toasts]);

  const pushRejections = useCallback((rejections: FlushRejection[]) => {
    if (rejections.length === 0) return;
    const now = Date.now();
    const next: SyncError[] = rejections.map((r) => ({
      ...r,
      id: `${now}-${errSeqRef.current++}`,
      at: now,
    }));
    // Garde au plus 5 erreurs récentes pour éviter d'empiler.
    setSyncErrors((prev) => [...prev, ...next].slice(-5));
  }, []);

  const dismissSyncError = useCallback((id: string) => {
    setSyncErrors((prev) => prev.filter((e) => e.id !== id));
  }, []);

  // Auto-dismiss après 10s.
  useEffect(() => {
    if (syncErrors.length === 0) return;
    const t = setTimeout(() => {
      const cutoff = Date.now() - 10_000;
      setSyncErrors((prev) => prev.filter((e) => e.at > cutoff));
    }, 10_000);
    return () => clearTimeout(t);
  }, [syncErrors]);

  // Init service on auth
  useEffect(() => {
    let active = true;
    if (session.status !== "authed") {
      setService(null);
      clearSentryUser();
      return;
    }
    setSentryUser(session.session.user.id);
    OfflineSyncService.init({
      userId: session.session.user.id,
      transport: apiSyncTransport,
      observer: {
        ...sentrySyncObserver,
        // Tâches retirées du snapshot (dé-assignation par le conducteur de
        // travaux) : le local est purgé silencieusement, on prévient l'utilisateur.
        onTasksPurged: (e) => {
          sentrySyncObserver.onTasksPurged?.(e);
          pushToast({
            kind: "info",
            message:
              e.taskIds.length > 1
                ? "Des missions vous ont été retirées."
                : "Une mission vous a été retirée.",
          });
        },
      },
    }).then((svc) => {
      if (!active) return;
      setService(svc);
    });
    return () => {
      active = false;
    };
  }, [session, pushToast]);

  // Online/offline events
  useEffect(() => {
    function handleOnline() {
      setOnline(true);
    }
    function handleOffline() {
      setOnline(false);
    }
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Initial fetch + flush quand on revient online. La loop sert aussi de
  // sonde de santé serveur : ses erreurs réseau/5xx flippent serverReachable.
  // Cadence raccourcie à 15s tant que le serveur est injoignable pour une
  // détection de recovery rapide ; 30s en régime nominal.
  useEffect(() => {
    if (!service) return;
    let cancelled = false;

    async function loop() {
      if (!service) return;
      if (!online) {
        setQueueLength(await service.queueLength());
        return;
      }
      try {
        await Sentry.startSpan({ name: "sync.cycle", op: "sync" }, async () => {
          await service.refreshFromServer();
          await service.refreshCatalogue("vt-upsell");
          await service.uploadPendingForSubmittedTasks();
          const res = await service.flush();
          if (cancelled) return;
          setServerReachable(true);
          setQueueLength(await service.queueLength());
          setLastSync(new Date().toISOString());
          pushRejections(res.rejections);
          if (res.completed.length > 0) {
            await service.refreshFromServer();
          }
          // Dégonflage rétroactif des blobs déjà uploadés, par petits lots, en
          // toute fin de cycle : purement du stockage, jamais prioritaire sur
          // la remontée terrain, et son échec ne doit pas marquer le cycle en
          // erreur (un appareil saturé est justement celui qui en a besoin).
          await service.reclaimUploadedBlobs().catch(() => 0);
          bumpTick();
        });
      } catch (err) {
        if (isServerUnreachable(err)) setServerReachable(false);
        console.warn("[offline] refresh/upload/flush failed", err);
      }
    }
    loop();

    const t = setInterval(loop, serverReachable ? 30_000 : 15_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [service, online, serverReachable, pushRejections, bumpTick]);

  const connectionState: ConnectionState = !online
    ? "offline"
    : !serverReachable
      ? "server-unavailable"
      : "online";

  // Tag Sentry suivi en continu : permet de corréler une erreur à l'état réseau
  // vu du terrain (online / offline / serveur injoignable).
  useEffect(() => {
    setConnectionStateTag(connectionState);
  }, [connectionState]);

  const value = useMemo<OfflineCtx>(
    () => ({
      service,
      queueLength,
      tick,
      online,
      serverReachable,
      connectionState,
      lastSync,
      syncErrors,
      dismissSyncError,
      toasts,
      pushToast,
      dismissToast,
      refresh: async () => {
        if (!service) return;
        try {
          await service.refreshFromServer();
          setServerReachable(true);
        } catch (err) {
          if (isServerUnreachable(err)) setServerReachable(false);
          throw err;
        }
        setQueueLength(await service.queueLength());
      },
      flush: async () => {
        if (!service) return;
        let res: FlushResult | undefined;
        try {
          const cycle = await service.syncNow();
          res = cycle.flush;
          setServerReachable(true);
        } catch (err) {
          if (isServerUnreachable(err)) setServerReachable(false);
          throw err;
        }
        setQueueLength(await service.queueLength());
        setLastSync(new Date().toISOString());
        if (res) pushRejections(res.rejections);
        bumpTick();
      },
      enqueue: async (m) => {
        if (!service) throw new Error("offline service not ready");
        const id = await service.enqueue(m);
        bumpTick();
        setQueueLength(await service.queueLength());
        return id;
      },
    }),
    [
      service,
      queueLength,
      tick,
      online,
      serverReachable,
      connectionState,
      lastSync,
      syncErrors,
      dismissSyncError,
      toasts,
      pushToast,
      dismissToast,
      pushRejections,
      bumpTick,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOffline(): OfflineCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useOffline outside OfflineProvider");
  return ctx;
}
